import { Auth0Error, WebAuth } from 'auth0-js';
import { FormEvent, useCallback, useEffect, useState } from 'react';
import Heading from '../../components/heading';
import Input, { InputGroup } from '../../components/input';
import Button from '../../components/button';
import { useGlobalState } from '../globalState/GlobalStateProvider';
import Message, { IMessage } from '../../components/message';
import ButtonGroup from '../../components/buttonGroup';
import styles from './registration.module.css';
import checkPasswordValidity from '../../helpers/passwordValidity';
import PasswordValidityHint from '../../components/passwordValidity';

interface RegistrationProps {
  auth0: WebAuth;
}

const Registration: React.FC<RegistrationProps> = ({
  auth0,
}: RegistrationProps) => {
  const { state, setState } = useGlobalState();
  const { email } = state;

  const [ready, setReady] = useState(true);
  const [messages, setMessages] = useState<IMessage | null>(null);

  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [familyNameAffix, setFamilyNameAffix] = useState('');
  const [password, setPassword] = useState('');
  const [passwordValid, setPasswordValid] = useState(
    checkPasswordValidity(password),
  );
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  useEffect(() => {
    setPasswordValid(checkPasswordValidity(password));
  }, [password]);

  const handleBack = useCallback(() => {
    setState({
      ...state,
      page: 'identifier',
    });
  }, [setState, state]);

  function handleLoginCallback(error: Auth0Error | null) {
    if (error) {
      // On error, redirect back to main. User should be able to login there, or get a better error
      setState({
        ...state,
        page: 'identifier',
      });
    }
  }

  function handleSubmitCallback(error: Auth0Error | null) {
    if (!error) {
      auth0.login(
        {
          email,
          password,
          realm: 'Personen',
        },
        handleLoginCallback,
      );
    } else {
      let errorMessage;
      switch (error.code) {
        case 'invalid_password':
          errorMessage = 'Je wachtwoord is niet sterk genoeg';
          break;
        default:
          errorMessage = 'Er gaat iets fout bij het aanmaken van je account...';
          break;
      }
      setMessages({
        type: 'error',
        text: errorMessage,
      });
      setReady(true);
    }
  }

  function isNameValid(name?: string): boolean {
    if (!name || name.length < 2) {
      return false;
    }
    return true;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (
      email &&
      passwordValid.valid &&
      isNameValid(givenName) &&
      isNameValid(familyName) &&
      agreeToTerms &&
      ready
    ) {
      setReady(false);
      auth0.signup(
        {
          email,
          password,
          connection: 'Personen',
          userMetadata: {
            given_name: givenName,
            family_name: familyName,
            family_name_affix: familyNameAffix,
            agreeToTerms: agreeToTerms.toString(),
          },
        },
        handleSubmitCallback,
      );
    } else if (!passwordValid.valid) {
      setMessages({
        type: 'error',
        text: 'Je wachtwoord is niet sterk genoeg',
      });
    } else if (!isNameValid(givenName) || !isNameValid(familyName)) {
      setMessages({
        type: 'error',
        text: 'Heb je je naam helemaal ingevuld?',
      });
    }
  }

  return (
    <>
      <Heading title="Maak je eigen CKC-account">
        <div>
          <p>
            Vul hieronder jouw gegevens in. Gegevens van je kinderen of andere
            gezinsleden voeg je later toe.
          </p>
        </div>
      </Heading>
      <Message message={messages} />
      <form onSubmit={handleSubmit}>
        <Input
          type="email"
          readOnly
          label="E-mail"
          name="email"
          value={email}
          onEdit={handleBack}
        />
        <Input
          type="text"
          label="Voornaam"
          name="givenName"
          value={givenName}
          onChange={(e) => setGivenName(e.target.value)}
          autoComplete="given-name"
          autoFocus
        />
        <InputGroup>
          <Input
            type="text"
            label="Tussenvoegsel"
            name="familyNameAffix"
            value={familyNameAffix}
            onChange={(e) => setFamilyNameAffix(e.target.value)}
            autoComplete="family-name-affix"
            optional
          />
          <Input
            type="text"
            label="Achternaam"
            name="familyName"
            value={familyName}
            onChange={(e) => setFamilyName(e.target.value)}
            autoComplete="family-name"
          />
        </InputGroup>
        <Input
          type="password"
          label="Wachtwoord"
          name="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
        />
        <PasswordValidityHint passwordValid={passwordValid} />
        <div className={styles.checkmark}>
          <input
            type="checkbox"
            name="agreeToTerms"
            id="agreeToTerms"
            checked={agreeToTerms}
            onChange={(e) => setAgreeToTerms(e.target.checked)}
            required
          />
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="agreeToTerms">
            Ik ga akkoord met de{' '}
            <a
              href="https://ckc-zoetermeer.nl/service/algemene-voorwaarden/"
              rel="noreferrer"
              target="_blank"
            >
              algemene voorwaarden
            </a>{' '}
            en het{' '}
            <a
              href="https://ckc-zoetermeer.nl/service/privacy/"
              rel="noreferrer"
              target="_blank"
            >
              privacybeleid
            </a>
          </label>
        </div>
        <ButtonGroup>
          <Button type="submit" formReady={ready}>
            Volgende
          </Button>
        </ButtonGroup>
      </form>
    </>
  );
};

export default Registration;
